// ...existing code...
import Papa from "papaparse";
import { Plus, Menu, Tag } from "lucide-react";
import { useState, useMemo, useEffect } from "react";
import BulkUploadSection from "../components/BulkUploadSection";
import CategoryManagementModal from "../components/CategoryManagementModal";
import AddItemModal from "../components/AddItemModal";
import ControlsBar from "../components/ControlsBar";
import MenuItemsDisplay from "../components/MenuItemsDisplay";
import api from "../services/apiService";

const DEFAULT_CATEGORIES = [
  { id: "appetizers", name: "Appetizers", color: "#EF4444" },
  { id: "mains", name: "Main Courses", color: "#F97316" },
  { id: "pasta", name: "Pasta", color: "#EAB308" },
  { id: "pizza", name: "Pizza", color: "#22C55E" },
  { id: "sushi", name: "Sushi", color: "#06B6D4" },
  { id: "ramen", name: "Ramen", color: "#8B5CF6" },
  { id: "desserts", name: "Desserts", color: "#EC4899" },
  { id: "beverages", name: "Beverages", color: "#6366F1" },
];

const MenuManager = ({
  menuItems = [],
  setMenuItems = () => {},
  handleAddMenuItem = () => {},
  handleAddMenuItems, // batch add handler (optional)
  handleDeleteMenuItem = () => {},
  showAddItem = false,
  setShowAddItem = () => {},
  newItem = {
    name: "",
    description: "",
    price: "",
    category: "mains",
    image: "",
  },
  setNewItem = () => {},
  restaurantId,
}) => {
  // Bulk upload state
  const [uploadItems, setUploadItems] = useState([]);
  const [uploadError, setUploadError] = useState("");
  // Edit modal state and handlers (must be inside component)
  const [showEditModal, setShowEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const handleEditMenuItem = (item) => {
    setEditItem(item);
    setShowEditModal(true);
  };
  const handleUpdateMenuItem = async (updatedItem) => {
    if (!updatedItem || !updatedItem.id || !restaurantId) return;
    try {
      const token = localStorage.getItem("token");
      const body = {
        id: updatedItem.id,
        name: updatedItem.name,
        description: updatedItem.description,
        type: updatedItem.type,
        price: updatedItem.price,
        quantity: updatedItem.quantity,
        category: updatedItem.category,
        image: updatedItem.image,
      };
      await api.put(`menu/update/${updatedItem.id}`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Fetch updated menu items after update
      const menuRes = await api.get(`menu/restaurant/${restaurantId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMenuItems(menuRes.data);
      setShowEditModal(false);
      setEditItem(null);
    } catch (err) {
      alert("Failed to update menu item");
    }
  };

  // Handle file upload and parse
  const handleFileUpload = async (e) => {
    console.log("File upload triggered");
    setUploadError("");
    const file = e.target.files[0];
    if (!file) {
      console.log("No file selected");
      return;
    }
    console.log("Selected file:", file.name);
    const ext = file.name.split(".").pop().toLowerCase();

    if (!["csv", "xlsx", "xls"].includes(ext)) {
      setUploadError("Only CSV or Excel files are supported.");
      console.log("Invalid file type:", ext);
      return;
    }

    try {
      console.log("Preparing to upload file...");
      const formData = new FormData();
      formData.append("file", file);

      const token = localStorage.getItem("token");
      console.log("Making API call to /api/menuItems/import");
      const response = await api.post("/menuItems/import", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      console.log("API Response:", response.data);
      if (response.data) {
        // Basic validation: must have name, price, category
        const items = response.data.filter(
          (item) => item.name && item.price && item.category
        );
        console.log("Processed items:", items);
        setUploadItems(items);
      }
    } catch (err) {
      console.error("Upload error:", err);
      setUploadError(
        "Failed to process file: " + (err.response?.data || err.message)
      );
    }
  };

  // Add all uploaded items
  const handleAddAllItems = async () => {
    try {
      const token = localStorage.getItem("token");
      await api.post(`menu/add/${restaurantId}`, uploadItems, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Fetch updated menu after adding items
      const menuRes = await api.get(`menu/restaurant/${restaurantId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMenuItems(menuRes.data);
      setUploadItems([]);
      setUploadError("");
      alert("All items added successfully!");
    } catch (err) {
      console.error("Error adding items:", err);
      setUploadError(
        "Failed to add items: " + (err.response?.data || err.message)
      );
    }
  };

  // ...existing state and logic (categories, search, etc.)...

  // Filtered items useMemo (top level)
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: "",
    color: "#10B981",
  });
  const [editingCategory, setEditingCategory] = useState(null);

  // Get all unique categories from menu items
  const menuCategories = useMemo(() => {
    const uniqueCategories = Array.from(
      new Set(menuItems.map((item) => item.category))
    );
    return uniqueCategories.map((catId) => {
      const existing = categories.find((c) => c.id === catId);
      return (
        existing || {
          id: catId,
          name: catId.charAt(0).toUpperCase() + catId.slice(1),
          color: "#6B7280",
        }
      );
    });
  }, [menuItems, categories]);

  // Combine default and menu categories
  const allCategories = useMemo(() => {
    const combined = [...categories];
    menuCategories.forEach((menuCat) => {
      if (!combined.find((c) => c.id === menuCat.id)) {
        combined.push(menuCat);
      }
    });
    return combined;
  }, [categories, menuCategories]);

  const filteredItems = useMemo(() => {
    let items = menuItems;
    if (selectedCategory !== "all") {
      items = items.filter((item) => item.category === selectedCategory);
    }
    if (search.trim()) {
      const searchTerm = search.trim().toLowerCase();
      items = items.filter(
        (item) =>
          item.name.toLowerCase().includes(searchTerm) ||
          (item.description &&
            item.description.toLowerCase().includes(searchTerm)) ||
          item.category.toLowerCase().includes(searchTerm)
      );
    }
    return items;
  }, [menuItems, selectedCategory, search]);

  // Category management handlers
  const handleAddCategory = () => {
    if (newCategory.name.trim()) {
      const categoryId = newCategory.name.toLowerCase().replace(/\s+/g, "-");
      const category = {
        id: categoryId,
        name: newCategory.name.trim(),
        color: newCategory.color,
      };
      setCategories((prev) => [...prev, category]);
      setNewCategory({ name: "", color: "#10B981" });
    }
  };

  const handleUpdateCategory = (categoryId, updatedCategory) => {
    setCategories(
      categories.map((cat) =>
        cat.id === categoryId ? { ...cat, ...updatedCategory } : cat
      )
    );
    setEditingCategory(null);
  };

  const handleDeleteCategory = (categoryId) => {
    setCategories(categories.filter((cat) => cat.id !== categoryId));
  };

  // Category stats
  const categoryStats = useMemo(() => {
    const stats = {};
    menuItems.forEach((item) => {
      stats[item.category] = (stats[item.category] || 0) + 1;
    });
    return stats;
  }, [menuItems]);

  const getCategoryColor = (categoryId) => {
    const category = allCategories.find((c) => c.id === categoryId);
    return category?.color || "#6B7280";
  };

  const getCategoryName = (categoryId) => {
    const category = allCategories.find((c) => c.id === categoryId);
    return (
      category?.name || categoryId.charAt(0).toUpperCase() + categoryId.slice(1)
    );
  };
  // Override handleDeleteMenuItem to use menu item ID only
  const handleDeleteMenuItemFixed = async (itemId) => {
    if (!itemId || !restaurantId) return;
    try {
      const token = localStorage.getItem("token");
      await api.delete(`menu/delete/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Fetch updated menu items after deleting
      const menuRes = await api.get(`menu/restaurant/${restaurantId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMenuItems(menuRes.data);
    } catch (err) {
      alert("Failed to delete menu item");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <div className="max-w-7xl mx-auto p-4 lg:p-6">
        {/* Header */}
        <div className="mb-8">
          <BulkUploadSection
            handleFileUpload={handleFileUpload}
            uploadError={uploadError}
            uploadItems={uploadItems}
            handleAddAllItems={handleAddAllItems}
          />
        </div>
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center">
              <Menu className="w-8 h-8 mr-3 text-emerald-600" />
              Menu Management
            </h1>
            <p className="text-gray-600">
              Manage your restaurant menu items and categories
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => setShowAddCategory(!showAddCategory)}
              className="px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all duration-200 flex items-center justify-center"
            >
              <Tag className="w-4 h-4 mr-2" />
              Manage Categories
            </button>
            <button
              onClick={() => setShowAddItem(!showAddItem)}
              className="px-6 py-2.5 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center"
            >
              <Plus className="w-3 h-3 mr-2" />
              Add Menu Item
            </button>
          </div>
        </div>
        <CategoryManagementModal
          showAddCategory={showAddCategory}
          setShowAddCategory={setShowAddCategory}
          newCategory={newCategory}
          setNewCategory={setNewCategory}
          handleAddCategory={handleAddCategory}
          allCategories={allCategories}
          editingCategory={editingCategory}
          setEditingCategory={setEditingCategory}
          handleUpdateCategory={handleUpdateCategory}
          handleDeleteCategory={handleDeleteCategory}
          categoryStats={categoryStats}
          DEFAULT_CATEGORIES={DEFAULT_CATEGORIES}
        />
        <ControlsBar
          search={search}
          setSearch={setSearch}
          viewMode={viewMode}
          setViewMode={setViewMode}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          showCategoryFilter={showCategoryFilter}
          setShowCategoryFilter={setShowCategoryFilter}
          allCategories={allCategories}
          getCategoryColor={getCategoryColor}
          getCategoryName={getCategoryName}
          menuItems={menuItems}
          categoryStats={categoryStats}
        />
      </div>
      <AddItemModal
        showAddItem={showAddItem}
        setShowAddItem={setShowAddItem}
        newItem={newItem}
        setNewItem={setNewItem}
        allCategories={allCategories}
        handleAddMenuItem={handleAddMenuItem}
      />
      <MenuItemsDisplay
        filteredItems={filteredItems}
        viewMode={viewMode}
        selectedCategory={selectedCategory}
        getCategoryColor={getCategoryColor}
        getCategoryName={getCategoryName}
        handleDeleteMenuItem={handleDeleteMenuItemFixed}
        handleEditMenuItem={handleEditMenuItem}
        restaurantId={restaurantId}
        setMenuItems={setMenuItems}
      />

      {/* Edit Modal (reuse AddItemModal for editing) */}
      {showEditModal && (
        <AddItemModal
          showAddItem={showEditModal}
          setShowAddItem={setShowEditModal}
          newItem={editItem}
          setNewItem={setEditItem}
          allCategories={allCategories}
          handleAddMenuItem={() => handleUpdateMenuItem(editItem)}
        />
      )}
    </div>
  );
};

export default MenuManager;
